<template>
  <div v-if="isOpen" class="modal modal-open">
    <div class="modal-box">
      <h3 class="text-lg font-bold">Tạo Bộ Cài Đặt Chiến Dịch</h3>
      <form @submit="onSubmit">
        <div class="grid grid-cols-1 gap-1">
          <!-- Fieldset 1 -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">
              Máy chủ mặc định <span class="text-error">*</span>
            </legend>
            <div class="flex items-start gap-2">
              <div class="w-full">
                <input
                  v-model="server_1_host"
                  type="text"
                  class="input w-full"
                  :class="{ 'input-error': errors.server_1_host }"
                  placeholder="Ví dụ: https://192.168.1.1"
                />
              </div>
              <div class="w-32">
                <input
                  v-model="server_1_port"
                  type="text"
                  class="input w-full"
                  :class="{ 'input-error': errors.server_1_port }"
                  placeholder="443"
                />
              </div>
            </div>
            <p v-if="errors.server_1_host" class="text-error mt-1 text-sm">
              {{ errors.server_1_host }}
            </p>
            <p v-if="errors.server_1_port" class="text-error mt-1 text-sm">
              {{ errors.server_1_port }}
            </p>
            <label class="fieldset-label">
              Lưu ý: Địa chỉ máy chủ phải bao gồm giao thức (http:// hoặc
              https://)
            </label>
          </fieldset>

          <!-- Fieldset 2 -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">
              Máy chủ dự phòng <span class="text-error">*</span>
            </legend>
            <div class="flex items-start gap-2">
              <div class="w-full">
                <input
                  v-model="server_2_host"
                  type="text"
                  class="input w-full"
                  :class="{ 'input-error': errors.server_2_host }"
                  placeholder="Ví dụ: https://192.168.1.2"
                />
              </div>
              <div class="w-32">
                <input
                  v-model="server_2_port"
                  type="text"
                  class="input w-full"
                  :class="{ 'input-error': errors.server_2_port }"
                  placeholder="443"
                />
              </div>
            </div>
            <p v-if="errors.server_2_host" class="text-error mt-1 text-sm">
              {{ errors.server_2_host }}
            </p>
            <p v-if="errors.server_2_port" class="text-error mt-1 text-sm">
              {{ errors.server_2_port }}
            </p>
            <label class="fieldset-label">
              Lưu ý: Địa chỉ máy chủ phải bao gồm giao thức (http:// hoặc
              https://)
            </label>
          </fieldset>

          <!-- Fieldset Name -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">
              Phiên bản bộ cài <span class="text-error">*</span>
            </legend>
            <div class="w-full">
              <input
                v-model="name"
                type="text"
                class="input w-full"
                :class="{ 'input-error': errors.name }"
                placeholder="Ví dụ: v1.0"
              />
              <p v-if="errors.name" class="text-error mt-1 text-sm">
                {{ errors.name }}
              </p>
            </div>
          </fieldset>
          <fieldset class="fieldset">
            <legend class="fieldset-legend">
              Loại bộ cài <span class="text-error">*</span>
            </legend>
            <div class="flex w-full flex-col gap-2">
              <label class="label cursor-pointer">
                <input type="checkbox" v-model="selectAll" class="checkbox" />
                <span class="label-text">Tất cả</span>
              </label>
              <label class="label cursor-pointer">
                <input
                  type="checkbox"
                  v-model="type"
                  value="side-loading"
                  class="checkbox"
                />
                <span class="label-text">side-loading</span>
              </label>
              <label class="label cursor-pointer">
                <input
                  type="checkbox"
                  v-model="type"
                  value="dropper"
                  class="checkbox"
                />
                <span class="label-text">dropper</span>
              </label>
              <p v-if="errors.type" class="text-error mt-1 text-sm">
                {{ errors.type }}
              </p>
            </div>
          </fieldset>
        </div>

        <div class="modal-action">
          <button type="button" class="btn" @click="$emit('close')">Hủy</button>
          <button
            type="submit"
            class="btn btn-primary disabled:btn-disabled"
            :disabled="isCreating || !meta.valid"
          >
            Tạo
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { createCampaignInstaller } from "@/api/campaign";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useForm } from "vee-validate";
import { computed } from "vue";
import { toast } from "vue3-toastify";

interface Props {
  isOpen: boolean;
  campaignId: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{ close: []; created: [] }>();
const queryClient = useQueryClient();

interface InstallerForm {
  name: string;
  type: string[];
  server_1_host: string;
  server_1_port: string;
  server_2_host: string;
  server_2_port: string;
}

const { defineField, handleSubmit, resetForm, meta, errors } =
  useForm<InstallerForm>({
    initialValues: {
      type: ["side-loading", "dropper"],
      server_1_host: window.location.protocol + "//" + window.location.hostname,
      server_1_port:
        window.location.port ||
        (window.location.protocol === "https:" ? "443" : "80"),
    },
    validationSchema: {
      name: "required",
      type: "required|min:1",
      server_1_host: "required|validHost",
      server_1_port: "required|isPort",
      server_2_host: "required|validHost",
      server_2_port: "required|isPort",
    },
  });

const [name] = defineField("name", {
  label: "Phiên bản bộ cài",
});
const [type] = defineField("type", {
  label: "Loại bộ cài",
});
const [server_1_host] = defineField("server_1_host", {
  label: "Địa chỉ mặc định",
});
const [server_1_port] = defineField("server_1_port", {
  label: "Cổng mặc định",
});
const [server_2_host] = defineField("server_2_host", {
  label: "Địa chỉ dự phòng",
});
const [server_2_port] = defineField("server_2_port", {
  label: "Cổng dự phòng",
});
const selectAll = computed({
  get: () => type.value.length === 2,
  set: (value) => {
    if (value) {
      type.value = ["side-loading", "dropper"];
    } else {
      type.value = [];
    }
  },
});

const { mutate: createInstaller, isPending: isCreating } = useMutation({
  mutationFn: createCampaignInstaller,
  onSuccess: () => {
    toast.success("Tạo bộ cài đặt chiến dịch thành công!");
    queryClient.invalidateQueries({ queryKey: ["stratistics"] });
    emit("created");
    emit("close");
    resetForm();
  },
  onError: (error) => {
    toast.error("Tạo bộ cài đặt chiến dịch thất bại!");
    console.error("Failed to create installer:", error);
  },
});

const onSubmit = handleSubmit((values) => {
  createInstaller({
    ...values,
    server_1_port: Number(values.server_1_port),
    server_2_port: Number(values.server_2_port),
    campaign: props.campaignId,
    md5_dll: "",
  });
});
</script>
